import { QueryClient } from "@tanstack/react-query";
import {
  json,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
} from "react-router-dom";

import { orgStorage } from "@/shared/auth";
import { RobotoUnauthorizedException } from "@/shared/http";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation/Goto";
import { layoutNavigationState } from "@/shared/navigation/layoutNavigation";
import { ErrorMonitoringService } from "@/shared/services";

import { UnrecoverableError } from "./exceptions";
import { visualizerResolvers } from "./resolvers";
import { VisualizerInitContext, VisualizerInitData } from "./visualizerInit";

/**
 * Implementation of react-router loader function
 * for the /visualize route.
 *
 * @link https://reactrouter.com/6.30.1/route/loader
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({ request, params }: LoaderFunctionArgs): Promise<Response> => {
    const url = new URL(request.url);

    const httpClient = await makeAuthenticatedHttpClient();
    const orgRecord = orgStorage.getCurrentOrg();

    // Redirect user to select orgs if org not found
    if (!orgRecord) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }

    const ctx: VisualizerInitContext = {
      httpClient,
      layoutFromNavState: layoutNavigationState.get(),
      layoutName: url.searchParams.get("layout") ?? undefined,
      orgId: orgRecord.org_id,
      queryClient,
      workspaceId: params.workspaceId,
    };

    for (const resolver of visualizerResolvers) {
      try {
        const result = await resolver(ctx);
        if (result !== null) {
          return json(result);
        }
      } catch (error) {
        if (error instanceof UnrecoverableError) {
          // Let the error boundary handle this error
          throw error;
        }

        if (error instanceof RobotoUnauthorizedException) {
          // User is authenticated but lacks permission to view this resource
          return redirect(`${Goto.route.Unauthorized}`);
        }

        // Log unexpected errors and continue
        ErrorMonitoringService.captureError(error);
      }
    }

    return json(null);
  };

/**
 * Implementation of react-router shouldRevalidate function
 * for the /visualize route.
 *
 * @link https://reactrouter.com/6.30.1/route/should-revalidate
 */
export function shouldRevalidate() {
  /**
   * This route doesn't rely on the url for managing state.
   * Therefore the loader should only be called once.
   */
  return false;
}

/**
 * Retrieve the loader data for the /visualize route.
 */
export function useWorkspaceLoaderData(): VisualizerInitData {
  return useLoaderData() as VisualizerInitData;
}
